import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import { useHistory } from 'react-router';
import { Button, FormControl, Grid, Typography } from '@mui/material';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles } from '@material-ui/core';
import { Field, Form } from 'react-final-form';
import { useState } from 'react';

import {
  selectUnidadeSelecionada,
  selectUnidadesDeNegocioAssociadas,
} from '../../../../features/usuario-logado/usuarioLogadoSlice';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import { GridItemModalProps } from '../../../common/components/cardsGrid/cardsModalGrid';
import { useUnidadeNegocioGuard } from '../../../common/hooks/useUnidadeNegocioGuard';
import { useListClientes } from '../../../common/hooks/useListClientes';
import MultiSelectStyled, { SimpleMultiSelectOption } from '../../../common/components/input/multiSelectStyled';
import QCXFinalDatePickerField from '../../../../shared-components/final-date-picker-field/QCXFinalDatePickerField';
import { generateKpiImportacaoReport } from './kpiImportacaoPage.helpers';

const useStyles = makeStyles({
  root: {
    '& .MuiDialog-paper': {
      minWidth: '50vw',
      minHeight: '40vh',
    },
  },
});

type Periodo = { inicio: string; fim: string };
type CampoPeriodo = 'abertura' | 'registro' | 'desembaraco' | 'faturamento';

const PERIODOS: { campo: CampoPeriodo; label: string }[] = [
  { campo: 'abertura', label: 'Data de Abertura' },
  { campo: 'registro', label: 'Data de Registro DI/DUIMP' },
  { campo: 'desembaraco', label: 'Data de Desembaraço' },
  { campo: 'faturamento', label: 'Data de Faturamento' },
];

const PERIODO_VAZIO: Periodo = { inicio: '', fim: '' };

const KpiImportacaoPage = ({ title, isOpen, closeModal }: GridItemModalProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;

  keycloak.updateToken(300);

  const selectedUnit = useSelector(selectUnidadeSelecionada);
  const tenantList = useSelector(selectUnidadesDeNegocioAssociadas);
  const [clientes] = useListClientes();

  const [selectedTenants, setSelectedTenants] = useState<SimpleMultiSelectOption[]>([]);
  const [selectedClients, setSelectedClients] = useState<SimpleMultiSelectOption[]>([]);
  const [periodos, setPeriodos] = useState<Record<CampoPeriodo, Periodo>>({
    abertura: PERIODO_VAZIO,
    registro: PERIODO_VAZIO,
    desembaraco: PERIODO_VAZIO,
    faturamento: PERIODO_VAZIO,
  });

  useUnidadeNegocioGuard();

  const handleChangePeriodo = (campo: CampoPeriodo, ponta: keyof Periodo) => (data: any) =>
    setPeriodos((anterior) => ({
      ...anterior,
      [campo]: { ...anterior[campo], [ponta]: data ? data.toISOString().split('T')[0] : '' },
    }));

  const toRange = ({ inicio, fim }: Periodo) => `${inicio};${fim}`;

  const handleSubmit = async () => {
    keycloak.updateToken(300);

    if (selectedTenants.length === 0) {
      dispatch(setErrorFeedback({ message: 'Selecione pelo menos uma Unidade de Negócio.' }));
      return;
    }

    const periodoInvertido = PERIODOS.find(({ campo }) => {
      const { inicio, fim } = periodos[campo];
      return inicio !== '' && fim !== '' && new Date(inicio) > new Date(fim);
    });

    if (periodoInvertido) {
      dispatch(
        setErrorFeedback({
          message: `A data inicial de ${periodoInvertido.label} não pode ser maior que a data final.`,
        })
      );
      return;
    }

    if (token && selectedUnit) {
      const erro = await generateKpiImportacaoReport(token, selectedUnit, {
        tenantIds: selectedTenants.map((t) => t.id.toString()),
        clientIds: selectedClients.map((c) => c.id.toString()),
        dataAberturaRange: toRange(periodos.abertura),
        dataRegistroRange: toRange(periodos.registro),
        dataDesembaracoRange: toRange(periodos.desembaraco),
        dataFaturamentoRange: toRange(periodos.faturamento),
      });

      if (erro == null) {
        dispatch(setSuccessFeedback({ message: 'Relatório gerado com sucesso!' }));
      } else {
        dispatch(setErrorFeedback({ message: erro }));
      }
    }

    history.push('/importacao/relatorios');
  };

  const renderDataField = (campo: CampoPeriodo, ponta: keyof Periodo, label: string) => (
    <Field
      name={`${campo}-${ponta}`}
      render={({ input }) => (
        <QCXFinalDatePickerField
          id={`kpi-importacao-${campo}-${ponta}`}
          name={input.name}
          label={`${label} (${ponta === 'inicio' ? 'Início' : 'Fim'})`}
          format="DD/MM/YYYY"
          placeholder="DD/MM/YYYY"
          onChange={(d: any) => {
            handleChangePeriodo(campo, ponta)(d);
            input.onChange(d);
          }}
          required={undefined}
          validate={undefined}
        />
      )}
    />
  );

  return (
    <Dialog className={classes.root} open={isOpen} onClose={closeModal}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Form onSubmit={() => {}}>
          {() => (
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={selectedTenants.length === 0}>
                    <MultiSelectStyled
                      controlledValues={selectedTenants}
                      options={tenantList.map((t: { id: { toString: () => any }; pessoa: { nomeResumido: any } }) => ({
                        id: t.id.toString(),
                        value: t.pessoa.nomeResumido,
                      }))}
                      onChangeAction={(_e: any, value: SimpleMultiSelectOption[] | null) =>
                        setSelectedTenants(value || [])
                      }
                      label="Unidade de Negócio"
                    />
                    {selectedTenants.length === 0 && (
                      <Typography variant="body2" color="error">
                        Este campo é obrigatório
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <MultiSelectStyled
                      controlledValues={selectedClients}
                      options={clientes.map((c) => ({
                        id: c.id ?? '',
                        value: `(${c.pessoa!.cnpj}) ${c.pessoa!.nome}`,
                      }))}
                      onChangeAction={(_e: any, value: SimpleMultiSelectOption[] | null) =>
                        setSelectedClients(value || [])
                      }
                      label="Clientes"
                    />
                  </FormControl>
                </Grid>

                {PERIODOS.map(({ campo, label }) => (
                  <Grid item xs={12} container spacing={2} key={campo}>
                    <Grid item xs={6}>
                      {renderDataField(campo, 'inicio', label)}
                    </Grid>
                    <Grid item xs={6}>
                      {renderDataField(campo, 'fim', label)}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </form>
          )}
        </Form>
      </DialogContent>
      <DialogActions>
        <Box padding={2} display="flex" gridGap={4}>
          <Button variant="outlined" color="primary" onClick={closeModal}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            fullWidth
            disabled={selectedTenants.length === 0}
          >
            Gerar Relatório
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default KpiImportacaoPage;
